import { Grid } from '@material-ui/core';
import useScrollButtonState from '@ncr-retail/hooks/build/useScrollButtonState/useScrollButtonState';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { itemMeasureAttributes } from 'src/common/helpers/item-quantity-measure';
import { TbrData } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { CartItemViewData } from 'src/items-list/business-logic/service/interfaces/cart-response';
import ActionWindowItemVoidLine from '../components/item-void-line';
import ActionWindowItemDetails from './action-window-item-details';
import { styles } from './action-window-styles';
import { BaseContainer } from './bottom-container/base-container';
import { Total } from './bottom-container/summary';
import ActionWindowItemIdLine from './item-id-line';
import ItemFees from './item-fees';
import ActionWindowLinkedLines from './linked-lines';
import TBRItemDetailLine from './tbr-item-detail';
import { isManualTareLineItem } from 'src/common/helpers/action-window-helper';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';

const ActionWindowItemLine: React.FC<Props> = (props: Props): JSX.Element => {
  const { item, tbrData, voidedQuantity = 0 } = props;
  const classes = styles({});
  const [{ currentPage, lastPage, onPageChanged }, scrollRef] = useScrollButtonState();
  const { t } = useTranslation();
  const { usePackIdentifier } = getCurrentXLRConfig();

  const isLinkedItemTare = item.linkedLines?.filter(
    i => i.parentLineLinkType === 'Tare' || i.itemType === 'Tare',
  ).length;

  const isManualTareItem = isManualTareLineItem(item);
  const isPackItem = item.lineItem?.quantity?.isPack;
  const UNIT_OF_MEASURE_EA = 'EA';
  const isWeighted = item.lineItem?.quantity?.unitOfMeasure !== UNIT_OF_MEASURE_EA;

  const {
    baseUnit,
    unitOfMeasure,
    baseFinalLinePrice,
    atomicMsuUnit,
    atomicMsuPrice,
    atomicMsuUnitForPrice,
    atomicOverridePrice,
    atomicOverrideUnit,
    isMsu,
    showPreviousPrice,
  } = itemMeasureAttributes(item, tbrData, props.isTBRActive);
  return (
    <>
      <Grid
        container
        className={`fullWidth ${classes.descriptionContainer}`}
        justifyContent='space-between'
      >
        <div
          ref={scrollRef}
          className={`verticalScroll fullWidth ${classes.customContainerHeight}`}
        >
          <Grid
            container
            className={classes.itemLineBottomMargin}
            data-testid='quantity-line-container'
          >
            {props.isTBRActive ? (
              <TBRItemDetailLine
                tbrData={tbrData}
                item={item}
                isTBRActive={props.isTBRActive}
                isWeighted={isWeighted}
              />
            ) : (
              <ActionWindowItemDetails
                baseUnit={baseUnit}
                unitOfMeasure={unitOfMeasure}
                isLinkedItemTare={isLinkedItemTare}
                atomicMsuPrice={atomicMsuPrice}
                atomicMsuUnitForPrice={atomicMsuUnitForPrice}
                atomicMsuUnit={atomicMsuUnit}
                isManualTareItem={isManualTareItem}
                isWeighted={isWeighted}
                isPackItem={isPackItem}
              />
            )}
            {!!item?.linkedLines?.length && !props.isTBRActive && (
              <Grid item className={classes.autoLeftMargin}>
                <span
                  className={classes.linkedItemPrice}
                  data-testid='action-window-item-unit-price'
                >
                  {props.isSelectedItemReturnItem ? '-' : ''}
                  {baseFinalLinePrice}
                </span>
              </Grid>
            )}
          </Grid>
          {showPreviousPrice && (
            <Grid container className={classes.fitHeight}>
              <Grid
                container
                justifyContent='space-between'
                className={`fullWidth ${classes.ItemDescriptionPadding}`}
              >
                <Grid item xs={6} container alignItems='center'>
                  <Grid item></Grid>
                  <Grid
                    item
                    className={classes.overrideLabel}
                    data-testid='action-window-previous-price'
                  >
                    {t('Previous_Price')}
                  </Grid>
                </Grid>
                <Grid item className={classes.priceOverride}>
                  <span
                    className={`${classes.autoLeftMargin} ${classes.priceOverrideText} ${classes.strikeThrough}`}
                    data-testid='action-window-override-price'
                  >
                    <span className={classes.price}>
                      {props.isSelectedItemReturnItem ? '-' : ''}
                      {atomicOverridePrice}
                    </span>
                    <span className={classes.perUnitText}>
                      {' / '}
                      {isPackItem && usePackIdentifier ? `${t('UOM_Pack')}` : atomicOverrideUnit}
                    </span>
                  </span>
                </Grid>
              </Grid>
            </Grid>
          )}
          {/* {isSelectedItemVoided &&
            (item.lineItem?.quantity?.unitOfMeasure ?? UnitOfMeasureTypes.Each) ===
              UnitOfMeasureTypes.Each && (
              <Grid container className={classes.fitHeight}>
                <Grid
                  container
                  justifyContent='space-between'
                  className={`fullWidth${classes.ItemDescriptionPadding}`}
                >
                  <Grid item xs={6} container alignItems='center'>
                    <Grid item>
                      <img src={voidItemImage} alt='ItemVoided' />
                    </Grid>
                    <Grid item className={classes.itemVoidedLabel}>
                      {t('ActionWindowItemVoided')}
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    className={classes.itemVoided}
                    data-testid='action-window-item-voided-text'
                  >
                    <span className={`${classes.autoLeftMargin} ${classes.itemVoidedText}`}>
                      <span className={classes.voidedCount}>{item.lineItem?.quantity?.value}</span>
                    </span>
                  </Grid>
                </Grid>
              </Grid>
            )} */}
          {item && voidedQuantity > 0 && !props.isCouponType && (
            <ActionWindowItemVoidLine
              voidQuantity={voidedQuantity}
              toggleKeypad={props.toggleKeypad}
              isKeypadVisible={props.isKeypadVisible}
            />
          )}
          {isMsu && (
            <Grid container className={classes.fitHeight}>
              <Grid
                container
                justifyContent='space-between'
                className={`fullWidth ${classes.ItemDescriptionPadding}`}
              >
                <Grid container className={classes.MSUContainer}>
                  <Grid item className={classes.price}>
                    Multiple Selling Units (MSU)
                  </Grid>
                </Grid>
                <Grid item className={classes.autoLeftMargin} data-testid='action-window-msu-price'>
                  <span className={classes.price}>
                    {props.isSelectedItemReturnItem ? '-' : ''}
                    {atomicMsuPrice}
                  </span>
                  <span className={classes.perUnitText}>
                    {` / ${atomicMsuUnitForPrice} ${atomicMsuUnit}`}
                  </span>
                </Grid>
              </Grid>
            </Grid>
          )}
          {!props.isTBRActive &&
            item?.linkedLines
              ?.filter(l => !!l.itemType)
              .map((lineitem: CartItemViewData) => (
                <ActionWindowLinkedLines
                  key={lineitem.description}
                  linkedLine={lineitem}
                  parentLine={item}
                  isSelectedItemReturnItem={props.isSelectedItemReturnItem}
                  atomicMsuUnit={lineitem.price?.unitOfMeasure ?? atomicMsuUnit}
                />
              ))}
          {item?.taxes && item.taxes?.length > 0 && <ItemFees taxes={item.taxes} />}
          {item?.lineItem?.itemId?.value && (
            <ActionWindowItemIdLine
              isKeypadVisible={props.isKeypadVisible}
              item={item}
              tbrData={tbrData}
              isTBRActive={props.isTBRActive}
            />
          )}
        </div>
      </Grid>
      <Grid className={`fullWidth ${classes.footer} ${classes.pushToBottom} `}>
        <BaseContainer
          toggleKeypad={props.toggleKeypad}
          isKeypadVisible={props.isKeypadVisible}
          currentPage={currentPage}
          totalPages={lastPage}
          updateCurrentPageHandler={onPageChanged}
        >
          <Total
            totalUnits={
              props.isTBRActive && tbrData.tbrAvailableQty
                ? tbrData.tbrAvailableQty
                : props.totalQuantity || 0
            }
            isSelectedItemReturnItem={props.isSelectedItemReturnItem}
            totalPages={lastPage}
          />
        </BaseContainer>
      </Grid>
    </>
  );
};

interface Props {
  isKeypadVisible: boolean | undefined;
  toggleKeypad: () => void;
  item: CartItemViewData;
  tbrData: TbrData;
  total: number;
  totalQuantity: number;
  isSelectedItemReturnItem?: boolean;
  isTareOverride: boolean;
  isTBRActive: boolean;
  isSelectedItemVoided: boolean;
  isCouponType: boolean;
  voidedQuantity?: number;
}

export default ActionWindowItemLine;
